import React from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import { STYLES } from "../mock";
import { trackHeroCta } from "../lib/analytics";

function Particles({ count = 14 }) {
  const particles = React.useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 4 + Math.random() * 6,
        duration: 14 + Math.random() * 12,
        delay: Math.random() * 10,
        opacity: 0.4 + Math.random() * 0.4,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <span
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: p.opacity,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Use Pop Art image as the cinematic blurred background
  const heroBg = STYLES.find((s) => s.id === "pop-art")?.image || STYLES[1]?.image;

  return (
    <section className="bg-radial-cream pt-32 pb-24 md:pt-44 md:pb-36 relative overflow-hidden">
      {/* Cinematic blurred background image */}
      <div
        className="hero-bg-image"
        style={{ "--hero-image": `url(${heroBg})` }}
      />
      {/* Soft color blobs */}
      <div className="pointer-events-none absolute -left-24 top-20 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#e8ddc3] to-transparent opacity-60 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 w-[520px] h-[520px] rounded-full bg-gradient-to-tr from-[#e8ddc3] to-transparent opacity-50 blur-3xl" />
      <Particles count={14} />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="fade-up inline-flex items-center gap-2 mb-8 px-5 py-2 rounded-full bg-[#e8ddc3]/80 border border-[#d6cdb9] text-xs md:text-sm tracking-wider text-[#6b5a35]">
          <Sparkles className="h-3.5 w-3.5 text-[#b59557]" />
          Arte Personalizado en Madera Blanca
        </div>

        <h1 className="fade-up d1 font-serif font-medium text-[#1a1816] text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[1.02] tracking-tight">
          Tu mascota,{" "}
          <span className="italic-accent">obra de arte</span>
        </h1>

        <p className="fade-up d2 mt-8 md:mt-10 text-base md:text-lg text-[#4a4136] max-w-2xl mx-auto leading-relaxed">
          Transformamos la foto de tu mejor amigo en un retrato único, impreso sobre un elegante
          bloque de madera blanca de 20×15×3 cm.
        </p>

        <div className="fade-up d2 mt-10 md:mt-12 inline-flex flex-col items-center gap-1 px-8 py-5 rounded-2xl bg-[#efe6cf]/70 border border-[#e2d5b3] backdrop-blur-sm">
          <div className="font-serif italic text-2xl md:text-3xl text-[#2a2620]">
            29,50€ — envío incluido
          </div>
          <div className="text-[11px] md:text-xs tracking-[0.2em] text-[#7a6a48] uppercase">
            Cualquier estilo · Península
          </div>
        </div>

        <div className="fade-up d3 mt-10 flex items-center justify-center gap-4 flex-wrap">
          <button
            onClick={() => {
              trackHeroCta();
              scrollTo("estilos");
            }}
            className="btn-primary rounded-full px-7 py-3.5 text-sm md:text-base font-medium"
            data-testid="hero-order-cta"
          >
            Pedir mi retrato
          </button>
          <button
            onClick={() => scrollTo("proceso")}
            className="btn-ghost rounded-full px-6 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
          >
            <ArrowDown className="h-4 w-4 text-[#b59557]" />
            Cómo Funciona
          </button>
        </div>

        {/* Trust strip */}
        <div className="fade-up d3 mt-14 flex items-center justify-center gap-6 md:gap-10 text-[11px] md:text-xs tracking-[0.16em] uppercase text-[#7a6a48]">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#b59557]" />
            Madera blanca
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#b59557]" />
            Tintas UV
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#b59557]" />
            Hecho en España
          </div>
        </div>
      </div>
    </section>
  );
}
